import React, { useEffect, useRef, useState } from 'react';
import './AnimatedLines.scss';

interface AnimatedLinesProps {
  /** Background color of the container */
  backgroundColor?: string;
  /** Array of colors for the animated lines */
  lineColors?: string[];
  /** Number of lines to render */
  lineCount?: number;
  /** Animation speed (lower is faster) */
  animationSpeed?: number;
  /** Line thickness */
  lineThickness?: number;
  /** Maximum curve amplitude */
  curveAmplitude?: number;
  /** Glow intensity */
  glowIntensity?: number;
  /** Animation duration in seconds */
  animationDuration?: number;
  /** Show vertical lines on sides */
  showVerticalLines?: boolean;
  /** Number of vertical lines on each side */
  verticalLineCount?: number;
  /** Curve direction for vertical lines (positive values curve inward) */
  verticalCurveAmplitude?: number;
  /** Horizontal line spacing factor (1 = evenly spaced, >1 = more space between lines) */
  horizontalLineSpacing?: number;
  /** Vertical line spacing factor (1 = evenly spaced, >1 = more space between lines) */
  verticalLineSpacing?: number;
  /** Distance from container edge where vertical lines start (% of container width) */
  verticalLineOffset?: number;
  /** Global opacity for all lines (0 to 1) */
  globalOpacity?: number;
}

interface Point {
  x: number;
  y: number;
  controlPointX?: number;
  controlPointY?: number;
}

interface LineData {
  points: Point[];
  color: string;
  type: 'horizontal' | 'vertical-left' | 'vertical-right';
}

const AnimatedLines: React.FC<AnimatedLinesProps> = ({
  backgroundColor = '#0f0f12',
  lineColors = ['#3a8bbb', '#64a6c3', '#8fc1cf', '#badddb'],
  lineCount = 5,
  animationSpeed = 0.5,
  lineThickness = 2,
  curveAmplitude = 30,
  glowIntensity = 4,
  animationDuration = 8,
  showVerticalLines = true,
  verticalLineCount = 3,
  verticalCurveAmplitude = 50,
  horizontalLineSpacing = 1.8,
  verticalLineSpacing = 2,
  verticalLineOffset = 10,
  globalOpacity = 1
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const requestRef = useRef<number>();
  const previousTimeRef = useRef<number>(0);
  const [dimensions, setDimensions] = useState({ width: 1000, height: 500 });
  const [animationOffset, setAnimationOffset] = useState(0);
  const linesRef = useRef<LineData[]>([]);

  // Function to generate initial line data for both horizontal and vertical lines
  const initializeLines = (width: number, height: number): LineData[] => {
    const newLines: LineData[] = [];
    
    // Generate horizontal lines with custom spacing
    const baseSpacing = height / (lineCount + 1);
    for (let i = 0; i < lineCount; i++) {
      // Apply spacing factor - higher values will push lines further apart from center
      const spacingFactor = horizontalLineSpacing;
      const normalizedPosition = (i + 1) / (lineCount + 1);
      
      // Calculate position with spacing factor:
      // - When spacingFactor = 1, lines are evenly spaced
      // - When spacingFactor > 1, lines get pushed away from center
      // - When spacingFactor < 1, lines get pulled toward center
      let positionFactor;
      if (normalizedPosition < 0.5) {
        // Bottom half - push down
        positionFactor = normalizedPosition * spacingFactor;
      } else {
        // Top half - push up
        positionFactor = 1 - ((1 - normalizedPosition) * spacingFactor);
      }
      
      // Clamp position factor to ensure lines stay within bounds
      positionFactor = Math.max(0.05, Math.min(0.95, positionFactor));
      
      const y = height * positionFactor;
      
      // Create a controlled curve with consistent amplitude
      const controlPointY = y + ((i % 2 === 0 ? 1 : -1) * curveAmplitude);
      
      const points: Point[] = [
        { x: 0, y, controlPointX: width / 2, controlPointY },
        { x: width, y }
      ];
      
      newLines.push({
        points,
        color: lineColors[i % lineColors.length],
        type: 'horizontal'
      });
    }
    
    // Generate vertical lines if enabled
    if (showVerticalLines && verticalLineCount > 0) {
      // Calculate offset from edges (as percentage of width)
      const edgeOffset = (width * verticalLineOffset) / 100;
      // Calculate available width for vertical lines
      const availableWidth = width - (edgeOffset * 2);
      
      // Left side vertical lines
      for (let i = 0; i < verticalLineCount; i++) {
        // Apply spacing factor for vertical lines
        const normalizedPosition = (i + 1) / (verticalLineCount + 1);
        const spacingFactor = verticalLineSpacing;
        
        let positionFactor;
        // Apply spacing with similar logic to horizontal lines
        if (normalizedPosition < 0.5) {
          positionFactor = normalizedPosition * spacingFactor;
        } else {
          positionFactor = 1 - ((1 - normalizedPosition) * spacingFactor);
        }
        
        // Clamp position factor
        positionFactor = Math.max(0.05, Math.min(0.95, positionFactor));
        
        // Position within the available space plus the edge offset
        const x = edgeOffset + (availableWidth * positionFactor / 2);
        
        // Control point for inward curve (positive amplitude curves inward)
        const controlPointX = x + verticalCurveAmplitude;
        
        const points: Point[] = [
          { x, y: 0, controlPointX, controlPointY: height / 2 },
          { x, y: height }
        ];
        
        newLines.push({
          points,
          color: lineColors[(i + lineCount) % lineColors.length],
          type: 'vertical-left'
        });
      }
      
      // Right side vertical lines
      for (let i = 0; i < verticalLineCount; i++) {
        // Apply spacing factor for vertical lines
        const normalizedPosition = (i + 1) / (verticalLineCount + 1);
        const spacingFactor = verticalLineSpacing;
        
        let positionFactor;
        // Apply spacing with similar logic to horizontal lines
        if (normalizedPosition < 0.5) {
          positionFactor = normalizedPosition * spacingFactor;
        } else {
          positionFactor = 1 - ((1 - normalizedPosition) * spacingFactor);
        }
        
        // Clamp position factor
        positionFactor = Math.max(0.05, Math.min(0.95, positionFactor));
        
        // Position within the available space plus the edge offset (from right side)
        const x = width - edgeOffset - (availableWidth * positionFactor / 2);
        
        // Control point for inward curve
        const controlPointX = x - verticalCurveAmplitude;
        
        const points: Point[] = [
          { x, y: 0, controlPointX, controlPointY: height / 2 },
          { x, y: height }
        ];
        
        newLines.push({
          points,
          color: lineColors[(i + lineCount + verticalLineCount) % lineColors.length],
          type: 'vertical-right'
        });
      }
    }
    
    return newLines;
  };

  // Generate path data using quadratic Bezier curves
  const getPathData = (line: LineData) => {
    if (!line.points.length || line.points.length < 2) return '';
    
    const { points } = line;
    const startPoint = points[0];
    const endPoint = points[1];
    const controlX = startPoint.controlPointX || (startPoint.x + endPoint.x) / 2;
    const controlY = startPoint.controlPointY || startPoint.y;
    
    return `M ${startPoint.x},${startPoint.y} Q ${controlX},${controlY} ${endPoint.x},${endPoint.y}`;
  };

  // Animation frame update function for gradient animation
  const animate = (time: number) => {
    if (previousTimeRef.current === 0) {
      previousTimeRef.current = time;
      requestRef.current = requestAnimationFrame(animate);
      return;
    }
    
    // Calculate elapsed time
    const deltaTime = time - previousTimeRef.current;
    previousTimeRef.current = time;
    
    // Update the animation offset for the gradient animation
    setAnimationOffset((prev) => {
      const newOffset = prev + (deltaTime * 0.05 * animationSpeed);
      // Keep it in a reasonable range but never reset to avoid jumps
      return newOffset % 2000; 
    });
    
    requestRef.current = requestAnimationFrame(animate);
  };
  
  // Use an intersection observer to optimize animation when not visible
  useEffect(() => {
    let observer: IntersectionObserver;
    
    const startAnimation = () => {
      if (!requestRef.current) {
        previousTimeRef.current = 0;
        requestRef.current = requestAnimationFrame(animate);
      }
    };
    
    const stopAnimation = () => {
      if (requestRef.current) {
        cancelAnimationFrame(requestRef.current);
        requestRef.current = undefined;
      }
    };
    
    // Only use IntersectionObserver if it's available in the browser
    if (typeof IntersectionObserver !== 'undefined' && svgRef.current) {
      observer = new IntersectionObserver((entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          startAnimation();
        } else {
          stopAnimation();
        }
      }, { threshold: 0.1 });
      
      observer.observe(svgRef.current);
    } else {
      // Fallback if IntersectionObserver is not available
      startAnimation();
    }
    
    // Start animation immediately
    startAnimation();
    
    return () => {
      stopAnimation();
      if (observer) {
        observer.disconnect();
      }
    };
  }, [animationSpeed]);

  // Handle resize events
  useEffect(() => {
    const handleResize = () => {
      if (svgRef.current) {
        const rect = svgRef.current.getBoundingClientRect();
        const newWidth = rect.width || window.innerWidth;
        const newHeight = rect.height || window.innerHeight;
        
        setDimensions({ width: newWidth, height: newHeight });
        linesRef.current = initializeLines(newWidth, newHeight);
      }
    };
    
    // Initial setup
    handleResize();
    
    // Set up resize handler
    window.addEventListener('resize', handleResize);
    
    // Remove resize handler on cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, [lineCount, lineColors, curveAmplitude, showVerticalLines, verticalLineCount, 
      verticalCurveAmplitude, horizontalLineSpacing, verticalLineSpacing, verticalLineOffset]);

  // Create SVG filter and gradient definitions
  const createDefinitions = () => {
    return (
      <defs>
        {/* Glow filter */}
        <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation={glowIntensity} result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        
        {/* Base gradients for each line with animation */}
        {lineColors.map((color, index) => (
          <linearGradient 
            key={`gradient-${index}`} 
            id={`gradient-${index}`} 
            gradientUnits="userSpaceOnUse"
            spreadMethod="repeat"
          >
            {/* Use dynamic animation keyframes based on the current animation offset */}
            <animate 
              attributeName="x1" 
              from={-dimensions.width} 
              to="0" 
              dur={`${animationDuration}s`}
              repeatCount="indefinite"
              values={`${-dimensions.width + animationOffset % dimensions.width}; 0; ${dimensions.width}`}
              keyTimes="0; 0.5; 1"
            />
            <animate 
              attributeName="x2" 
              from="0" 
              to={dimensions.width} 
              dur={`${animationDuration}s`}
              repeatCount="indefinite"
              values={`0; ${dimensions.width}; ${dimensions.width * 2}`}
              keyTimes="0; 0.5; 1"
            />
            
            {/* Gradient stops with more vibrant transitions */}
            <stop offset="0%" stopColor={lineColors[index % lineColors.length]} />
            <stop offset="25%" stopColor={lineColors[(index + 1) % lineColors.length]} />
            <stop offset="50%" stopColor={lineColors[(index + 2) % lineColors.length]} />
            <stop offset="75%" stopColor={lineColors[(index + 1) % lineColors.length]} />
            <stop offset="100%" stopColor={lineColors[index % lineColors.length]} />
          </linearGradient>
        ))}
        
        {/* Vertical gradient for vertical lines */}
        {lineColors.map((color, index) => (
          <linearGradient 
            key={`vertical-gradient-${index}`} 
            id={`vertical-gradient-${index}`} 
            gradientUnits="userSpaceOnUse"
            gradientTransform="rotate(90)"
            spreadMethod="repeat"
          >
            {/* Use dynamic animation keyframes based on the current animation offset */}
            <animate 
              attributeName="y1" 
              from={-dimensions.height} 
              to="0" 
              dur={`${animationDuration * 1.2}s`}
              repeatCount="indefinite"
              values={`${-dimensions.height + animationOffset % dimensions.height}; 0; ${dimensions.height}`}
              keyTimes="0; 0.5; 1"
            />
            <animate 
              attributeName="y2" 
              from="0" 
              to={dimensions.height} 
              dur={`${animationDuration * 1.2}s`}
              repeatCount="indefinite"
              values={`0; ${dimensions.height}; ${dimensions.height * 2}`}
              keyTimes="0; 0.5; 1"
            />
            
            {/* Gradient stops with more vibrant transitions */}
            <stop offset="0%" stopColor={lineColors[index % lineColors.length]} />
            <stop offset="25%" stopColor={lineColors[(index + 1) % lineColors.length]} />
            <stop offset="50%" stopColor={lineColors[(index + 2) % lineColors.length]} />
            <stop offset="75%" stopColor={lineColors[(index + 1) % lineColors.length]} />
            <stop offset="100%" stopColor={lineColors[index % lineColors.length]} />
          </linearGradient>
        ))}
        
        {/* Highlight gradient for glow effect */}
        <linearGradient 
          id="highlightGradient" 
          gradientUnits="userSpaceOnUse"
        >
          <animate 
            attributeName="x1" 
            from={-dimensions.width} 
            to={dimensions.width * 2} 
            dur={`${animationDuration * 0.7}s`}
            repeatCount="indefinite" 
          />
          <animate 
            attributeName="x2" 
            from="0" 
            to={dimensions.width * 3} 
            dur={`${animationDuration * 0.7}s`}
            repeatCount="indefinite" 
          />
          
          <stop offset="0%" stopColor="white" stopOpacity="0" />
          <stop offset="25%" stopColor="white" stopOpacity="0.1" />
          <stop offset="50%" stopColor="white" stopOpacity="0.4" />
          <stop offset="75%" stopColor="white" stopOpacity="0.1" />
          <stop offset="100%" stopColor="white" stopOpacity="0" />
        </linearGradient>
        
        {/* Vertical highlight gradient */}
        <linearGradient 
          id="verticalHighlightGradient" 
          gradientUnits="userSpaceOnUse"
          gradientTransform="rotate(90)"
        >
          <animate 
            attributeName="y1" 
            from={-dimensions.height} 
            to={dimensions.height * 2} 
            dur={`${animationDuration * 0.8}s`}
            repeatCount="indefinite" 
          />
          <animate 
            attributeName="y2" 
            from="0" 
            to={dimensions.height * 3} 
            dur={`${animationDuration * 0.8}s`}
            repeatCount="indefinite" 
          />
          
          <stop offset="0%" stopColor="white" stopOpacity="0" />
          <stop offset="25%" stopColor="white" stopOpacity="0.1" />
          <stop offset="50%" stopColor="white" stopOpacity="0.4" />
          <stop offset="75%" stopColor="white" stopOpacity="0.1" />
          <stop offset="100%" stopColor="white" stopOpacity="0" />
        </linearGradient>
      </defs>
    );
  };

  const containerStyle = {
    backgroundColor
  };

  return (
    <div className="animated-lines container" style={containerStyle}>
      <svg 
        ref={svgRef} 
        preserveAspectRatio="none"
        className="svg"
        viewBox={`0 0 ${dimensions.width || 1000} ${dimensions.height || 500}`}
      >
        {createDefinitions()}
        {linesRef.current.map((line, index) => {
          const isVertical = line.type === 'vertical-left' || line.type === 'vertical-right';
          const gradientId = isVertical 
            ? `vertical-gradient-${index % lineColors.length}`
            : `gradient-${index % lineColors.length}`;
            
          const highlightGradientId = isVertical 
            ? "verticalHighlightGradient"
            : "highlightGradient";
            
          return (
            <React.Fragment key={`${line.type}-${index}`}>
              {/* Base line with animated gradient */}
              <path
                d={getPathData(line)}
                stroke={`url(#${gradientId})`}
                strokeWidth={lineThickness}
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                opacity={globalOpacity}
              />
              {/* Glow highlight overlay */}
              <path
                d={getPathData(line)}
                stroke={`url(#${highlightGradientId})`}
                strokeWidth={lineThickness * 0.8}
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                filter="url(#glow)"
                opacity={globalOpacity * 0.7}
              />
            </React.Fragment>
          );
        })}
      </svg>
    </div>
  );
};

export { AnimatedLines };
export default AnimatedLines;
